import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';

import { RequestService } from '../request';
import {
  IOption,
  ICenterRaw,
  IServiceRaw,
  ITargetRaw,
  IDianosisRaw,
  IAreaRaw,
  ILabRaw,
  ITipoPeticionRaw,
  ICreateExportOptions,
  Determinacion,
  PivotMethod,
  IAntibioticoRaw,
  ISensibilidadRaw,
  IQueryBuilderConfig,
} from './defs';
import { HttpParams } from '@angular/common/http';
import { CustomResponse } from '../defs';

@Injectable({
  providedIn: 'root',
})
export class ClinicalTrialsService {
  private baseUrl = '/api/estudios-clinicos';

  constructor(private request: RequestService) {}

  public getCenters(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/centros`).pipe(
      map((rawCenters: ICenterRaw[]) => {
        return rawCenters
          .map((center) => ({
            id: center.skuCentro,
            description: center.descLCentro ?? center.descCentro,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getServices(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/servicios`).pipe(
      map((rawServices: IServiceRaw[]) => {
        return rawServices
          .map((center) => ({
            id: center.skuServicio,
            description: center.descLServicio ?? center.descServicio,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getTargets(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/destinos`).pipe(
      map((rawServices: ITargetRaw[]) => {
        return rawServices
          .map((center) => ({
            id: center.skuDestino,
            description: center.descLDestino ?? center.descDestino,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getTiposPeticion(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/tipos-peticion`).pipe(
      map((rawServices: ITipoPeticionRaw[]) => {
        return rawServices
          .map((tipoPeticion) => ({
            id: tipoPeticion.skuTipoPeticion,
            description: tipoPeticion.descLTipoPeticion ?? tipoPeticion.descTipoPeticion,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getDiagnosis(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/diagnosticos`).pipe(
      map((rawServices: IDianosisRaw[]) => {
        return rawServices
          .map((center) => ({
            id: center.skuDiagnostico,
            description: center.descLDiagnostico ?? center.descDiagnostico,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getAreas(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/areas`).pipe(
      map((rawServices: IAreaRaw[]) => {
        return rawServices
          .map((center) => ({
            id: center.skuAreas,
            description: center.descLAreas ?? center.descAreas,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getSensibilidades(): Observable<string[]> {
    return this.request
      .get<ISensibilidadRaw[]>(`${this.baseUrl}/sensibilidades`)
      .pipe(
        map((raw) => {
          return raw.map((r) => r.descLSensibilidad);
        })
      );
  }

  public getQueryBuilderConfig(): Observable<IQueryBuilderConfig> {
    return this.request.get<IQueryBuilderConfig>(`${this.baseUrl}/config`);
  }

  public getAntibioticos(): Observable<IOption[]> {
    return this.request.getNoLoading(`${this.baseUrl}/antibioticos`).pipe(
      map((rawServices: IAntibioticoRaw[]) => {
        return rawServices
          .map((antibiotic) => ({
            id: antibiotic.codAntibiotico,
            description:
              antibiotic.descLAntibiotico ?? antibiotic.descAntibiotico,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  public getLabs(): Observable<IOption[]> {
    return this.request.get(`${this.baseUrl}/laboratorios`).pipe(
      map((rawServices: ILabRaw[]) => {
        return rawServices
          .map((center) => ({
            id: center.skuLaboratorio,
            description: center.descLLaboratorio ?? center.descLaboratorio,
          }))
          .sort((a, b) => (a.description > b.description ? 1 : -1));
      })
    );
  }

  private pad(num, size) {
    num = num.toString();
    while (num.length < size) num = '0' + num;
    return num;
  }

  private formatDate(date: Date | string): string {
    const _date = new Date(date);
    return `${_date.getFullYear()}-${this.pad(
      _date.getMonth() + 1,
      2
    )}-${this.pad(_date.getDate(), 2)}`;
  }

  private getRange(dates: Date[]): string[] {
    const ret = [this.formatDate(dates[0])];
    if (dates.length > 1) {
      ret.push(this.formatDate(dates[1] || dates[0])); // if only one date, use the same, is the same day
    }
    return ret;
  }

  private getDateFromAge(age: number): Date {
    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - age);
    return currentDate;
  }

  private createExportObject(options: ICreateExportOptions, historicDatabaseName?: string): any {
    const request = {};

    if (options.registerRange) {
      request['i.fechaRegistro'] = this.getRange(options.registerRange);
    }

    if (options.gender) {
      request['pa.sexo'] = options.gender;
    }

    if (options.ageFrom != null) {
      request['pa.fechaNac'] = [
        this.formatDate(this.getDateFromAge(options.ageFrom)),
      ];
    }

    if (options.ageTo != null) {
      if (!request['pa.fechaNac']) {
        request['pa.fechaNac'] = [null];
      }

      request['pa.fechaNac'].push(
        this.formatDate(this.getDateFromAge(options.ageTo))
      );
    }

    if (options.serviceId) {
      request['s.skuServicio'] = options.serviceId;
    }

    if (options.targetId) {
      request['i.skuDestino'] = options.targetId;
    }

    if (options.centerId) {
      request['i.skuCentro'] = options.centerId;
    }

    if (options.reasonRequest) {
      request['i.motivoSolicitud'] = `%${options.reasonRequest}%`;
    }

    if (options.diagnosisId) {
      request['i.skuDiagnostico'] = options.diagnosisId;
    }

    if (options.numPeticion && options.numPeticion.length) {
      request['i.numPeticion'] = options.numPeticion;
    }

    if (options.codPatient && options.codPatient.length) {
      request['codPaciente'] = options.codPatient;
    }

    if (options.urgent) {
      request['i.urgente'] = options.urgent;
    }

    if (options.areaId) {
      request['d.skuAreas'] = options.areaId;
    }

    if (options.labId) {
      request['i.skuLaboratorio'] = options.labId;
    }

    if (options.tipoPeticionId) {
      request['i.skuTipoPeticion'] = options.tipoPeticionId;
    }

    if (options.validationRange) {
      request['d.fechaValidacionDt'] = this.getRange(options.validationRange);
    }

    if (options.queryBuilder) {
      request['queryBuilder'] = options.queryBuilder;
    }

    if (options.methods) {
      request['methods'] = options.methods;
    }
    
    if (options.strictMethodMatch !== undefined) {
      request['strictMethodMatch'] = options.strictMethodMatch;
    }

    if (options.fields) {
      request['fields'] = options.fields;
    }

    if (options.comment) {
      request['i.info_1'] = `%${options.comment}%`;
    }

    if (options.validationRangeEpisode) {
      request['i.info4Dt'] = this.getRange(options.validationRangeEpisode);
    }

    if (options.episode) {
      request['i.info_5'] = `%${options.episode}%`;
    }

    if (options.priority) {
      request['i.info_6'] = `%${options.priority}%`;
    }

    if (options.target) {
      request['i.info_7'] = `%${options.target}%`;
    }

    if (options.externalSystem) {
      request['i.info_8'] = `%${options.externalSystem}%`;
    }

    if (options.info9) {
      request['i.info_9'] = `%${options.info9}%`;
    }

    if (options.notesHCIS) {
      request['i.attrib1'] = `%${options.notesHCIS}%`;
    }

    if (options.extractor) {
      request['i.attrib2'] = `%${options.extractor}%`;
    }

    if (options.appointments) {
      request['i.attrib3'] = `${options.appointments}`;
    }

    if (options.hcisUnity) {
      request['i.attrib5'] = `%${options.hcisUnity}%`;
    }

    if (options.demographicDataOnly !== undefined) {
      request['demographicDataOnly'] = options.demographicDataOnly;
    }

    return request;
  }

  public findPivotMethods(
    type: string,
    options: ICreateExportOptions,
    historicDatabaseName?: string | null
  ): Observable<{ methods: PivotMethod[]; debug?: { query: string } }> {
    if (type === 'MIC') {
      return of({ methods: [] });
    }
    const query = this.createExportObject(options);
    let params = new HttpParams();
    if (historicDatabaseName) {
      params = params.set('historicDatabaseName', historicDatabaseName);
    }
    return this.request.post<{ methods: PivotMethod[]; debug?: { query: string } }>(
      `${this.baseUrl}/determinaciones/pivot-methods`,
      query,
      { params }
    );
  }

  public createPivotExport(
    options: ICreateExportOptions,
    queryName: string,
    historicDatabaseName?: string | null
  ): Observable<{ id: number }> {
    const query: Record<string, unknown> = {
      ...this.createExportObject(options, historicDatabaseName),
      queryName,
    };
    let params = new HttpParams();
    if (historicDatabaseName) {
      params = params.set('historicDatabaseName', historicDatabaseName);
    }
    return this.request.post<{ id: number }>(
      `${this.baseUrl}/../export/determinaciones-pivot`,
      query as never,
      { params }
    );
  }

  public createExport(options: ICreateExportOptions, historicDatabaseName?: string): Observable<any> {
    const query = this.createExportObject(options, historicDatabaseName);
    let params = new HttpParams();

    if (historicDatabaseName) {
      params = params.set('historicDatabaseName', historicDatabaseName)
    }

    return this.request.post(
      `${this.baseUrl}/../export/determinaciones`,
      query,
      {
        params,
      }
    );
  }

  public findDeterminaciones(
    type: string,
    options: ICreateExportOptions,
    historicDatabaseName?: string | null,
    limit = 10,
    offset = 0,
    pivotByCodPeticion = false
  ): Observable<CustomResponse<Determinacion[]>> {
    const query = this.createExportObject(options);
    let params = new HttpParams();

    params = params.set('limit', limit.toString());
    params = params.set('offset', offset.toString());

    if (historicDatabaseName) {
      params = params.set('historicDatabaseName', historicDatabaseName)
    }

    if (pivotByCodPeticion && type !== 'MIC') {
      params = params.set('pivotByCodPeticion', 'true');
    }

    const typeSuffix = type === 'MIC' ? '-micro' : '';

    return this.request.post<Determinacion[]>(
      `${this.baseUrl}/determinaciones${typeSuffix}`,
      query,
      {
        params,
      }
    );
  }
}
